import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {setIssueFieldValueMutation} from './__generated__/setIssueFieldValueMutation.graphql'

export function commitSetIssueFieldValueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {
    issueId: string
    issueFields: Array<{
      fieldId: string
      textValue?: string | null
      singleSelectOptionId?: string | null
      delete?: boolean
    }>
  }
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation<setIssueFieldValueMutation>(environment, {
    mutation: graphql`
      mutation setIssueFieldValueMutation($input: SetIssueFieldValueInput!) {
        setIssueFieldValue(input: $input) {
          issueFieldValues {
            ... on IssueFieldTextValue {
              id
              value
            }
            ... on IssueFieldSingleSelectValue {
              id
              name
              color
              description
            }
          }
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input: {
        issueId: input.issueId,
        issueFields: input.issueFields,
      },
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: error => onError?.(error),
    updater: store => {
      const issue = store.get(input.issueId)
      const fieldValues = issue?.getLinkedRecord('issueFieldValues', {first: 25})
      const nodes = fieldValues?.getLinkedRecords('nodes') || []
      const payload = store.getRootField('setIssueFieldValue')
      const newValues = payload.getLinkedRecords('issueFieldValues') || []

      let updatedNodes = nodes.slice()
      for (const fieldInput of input.issueFields) {
        const prevNode = updatedNodes.find(node => {
          const field = node.getLinkedRecord('field')
          return field && field.getDataID() === fieldInput.fieldId
        })
        const newValue = newValues.find(nv => {
          if (!nv) return false
          let field = nv.getLinkedRecord('field')
          // If missing, set the field relation for matching
          if (!field) {
            const fieldRecord = store.get(fieldInput.fieldId)
            if (fieldRecord) nv.setLinkedRecord(fieldRecord, 'field')
            field = nv.getLinkedRecord('field')
          }
          return field && field.getDataID() === fieldInput.fieldId
        })

        if (!newValue && prevNode) {
          if (prevNode.getType() === 'IssueFieldTextValue') {
            prevNode.setValue('', 'value')
          } else if (prevNode.getType() === 'IssueFieldSingleSelectValue') {
            prevNode.setValue(null, 'name')
            prevNode.setValue(null, 'color')
            prevNode.setValue(null, 'description')
          }
          continue
        }

        let found = false
        updatedNodes = updatedNodes
          .map(node => {
            const field = node.getLinkedRecord('field')
            if (field && field.getDataID() === fieldInput.fieldId) {
              found = true
              return newValue
            }
            return node
          })
          .filter((n): n is NonNullable<typeof n> => !!n)
        if (!found && newValue) {
          updatedNodes = [...updatedNodes, newValue]
        }
      }

      // Ensure all newValues are present in updatedNodes (for new fields)
      for (const newValue of newValues) {
        if (!updatedNodes.some(node => node.getDataID() === newValue.getDataID())) {
          updatedNodes = [...updatedNodes, newValue]
        }
      }

      fieldValues?.setLinkedRecords(updatedNodes, 'nodes')
    },
  })
}
